/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';

import {
    Heading,
    VerificationCodeField,
    Label,
    Button,
    DividerNew,
    Icon,
    TextField,
} from 'sarsaparilla';
import { getMessageFromError } from 'sarsaparilla/src/utils';
import { postMfaToken, postMfaTokenErrors } from 'shared-ui/api/account';

import { TermsAndPrivacyPolicy } from '../components/TermsAndPrivacyPolicy';

import styles from './MfaCodeForms.module.scss';
import { MfaCodeErrors, type MfaCodeErrorType } from './MfaCodeErrors';
import { useAuthModalScreen } from '../AuthModalScreenContext';

export function MfaCodeForms() {
    const { closeModal, storeUserData } = useAuthModalScreen();

    const [mfaScreen, setMfaScreen] = React.useState<'mfaCode' | 'backupCode'>('mfaCode');
    const [errorType, setErrorType] = React.useState<MfaCodeErrorType>(null);
    const [isLoading, setIsLoading] = React.useState(false);
    const fieldRef = React.useRef<any>(null);
    const inputRef = React.useRef<HTMLInputElement>(null);

    const showCodeForm = () => {
        setMfaScreen('mfaCode');
        setErrorType(null);
    };

    const showBackupCodeForm = () => {
        setMfaScreen('backupCode');
        setErrorType(null);
    };

    const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsLoading(true);
        setErrorType(null);

        const validation = fieldRef.current?.validate();
        const formData = new FormData(event.currentTarget);
        const token = formData.get('token')?.toString().trim();

        if (validation?.isInvalid || !token) {
            fieldRef.current?.focus();
            inputRef.current?.focus();
            setIsLoading(false);
            return;
        }

        try {
            const { data } = await postMfaToken(token);
            // User is logged in - store user data in redux and local storage
            storeUserData(data);
            closeModal();
        } catch (error) {
            const errorMessage = getMessageFromError(error);
            if (
                errorMessage === postMfaTokenErrors.MFA_REJECTED ||
                errorMessage === postMfaTokenErrors.MFA_ERROR_CODE ||
                errorMessage === postMfaTokenErrors.INVALID_AUTH_TOKEN
            ) {
                setErrorType('invalidToken');
            } else {
                setErrorType('unknown');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="px-sm-4 pt-sm-4 pb-sm-1">
            <Heading
                headingLevel={1}
                appearance="h4"
                className="mb-1 pr-4"
                id="modal-heading"
            >
                Multi-Factor Authentication
            </Heading>

            <div className="pt-half">
                {mfaScreen === 'mfaCode' ? (
                    <p className="text-md mb-4">
                        Enter the code generated by your authenticator app to complete
                        your login.
                    </p>
                ) : (
                    <p className="text-md mb-4">
                        Enter one of the backup codes provided when you enrolled in
                        multi-factor authentication to complete your login.
                    </p>
                )}
            </div>

            <MfaCodeErrors errorType={errorType} />

            {mfaScreen === 'mfaCode' ? (
                <MfaCodeForm
                    showBackupCodeForm={showBackupCodeForm}
                    fieldRef={fieldRef}
                    isLoading={isLoading}
                    onSubmit={onSubmit}
                />
            ) : (
                <BackupCodeForm
                    showCodeForm={showCodeForm}
                    fieldRef={fieldRef}
                    inputRef={inputRef}
                    isLoading={isLoading}
                    onSubmit={onSubmit}
                />
            )}

            <DividerNew className="mt-4 mb-2" />

            <TermsAndPrivacyPolicy />
        </div>
    );
}

interface MfaCodeFormProps {
    showBackupCodeForm: () => void;
    fieldRef: React.RefObject<any>;
    isLoading: boolean;
    onSubmit: (event: React.FormEvent<HTMLFormElement>) => Promise<void>;
}

export function MfaCodeForm({
    showBackupCodeForm,
    fieldRef,
    isLoading,
    onSubmit,
}: MfaCodeFormProps) {
    return (
        <>
            <form onSubmit={onSubmit} className="mt-5">
                <div className={styles.fieldContainer}>
                    <Label
                        htmlFor="verification-code"
                        isRequired
                        className="mb-1 text-center"
                    >
                        6-digit Code from Authenticator App
                    </Label>
                    <VerificationCodeField
                        id="verification-code"
                        name="token"
                        label="Verification Code"
                        isRequired
                        autoComplete="one-time-code"
                        ref={fieldRef}
                    />
                </div>

                <Button
                    type="submit"
                    size="lg"
                    shouldFitContainer
                    className="mt-5"
                    isLoading={isLoading}
                >
                    Verify Code
                </Button>
            </form>

            <div className="text-sm text-center mt-3">
                Don‘t have access to your authenticator app?{' '}
            </div>
            <Button
                appearance="link"
                size="md"
                style={{ verticalAlign: 'baseline' }}
                iconAfterElement={<Icon iconName="chevron-right" />}
                onClick={showBackupCodeForm}
                shouldFitContainer
                isDisabled={isLoading}
            >
                Use a One-Time Backup Code
            </Button>
        </>
    );
}

interface BackupCodeFormProps {
    showCodeForm: () => void;
    fieldRef: React.RefObject<any>;
    inputRef: React.RefObject<HTMLInputElement>;
    isLoading: boolean;
    onSubmit: (event: React.FormEvent<HTMLFormElement>) => Promise<void>;
}

export function BackupCodeForm({
    showCodeForm,
    fieldRef,
    inputRef,
    isLoading,
    onSubmit,
}: BackupCodeFormProps) {
    return (
        <>
            <form onSubmit={onSubmit} className="mt-5">
                <TextField
                    id="backup-code"
                    name="token"
                    label="One-Time Backup Code"
                    isRequired
                    ref={fieldRef}
                    innerRef={inputRef}
                    autoComplete="off"
                />

                <Button
                    type="submit"
                    size="lg"
                    shouldFitContainer
                    className="mt-5"
                    isLoading={isLoading}
                >
                    Verify Code
                </Button>
            </form>

            <div className="text-md text-center mt-3">
                <Button
                    appearance="link"
                    size="md"
                    style={{ verticalAlign: 'baseline' }}
                    iconBeforeElement={<Icon iconName="chevron-left" />}
                    onClick={showCodeForm}
                    shouldFitContainer
                    isDisabled={isLoading}
                >
                    Enter Code from Authenticator App
                </Button>
            </div>
        </>
    );
}
